import React, { useEffect, useMemo, useState } from "react";
import {
    DataGridPremium,
    useGridApiRef,
    gridSortedRowIdsSelector,
} from "@mui/x-data-grid-premium";
import { useSeasonalForecastContext } from "../Contexts/seasonalForecastContext";
import dayjs from "dayjs";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import { isInt } from "../Utilities/utilities.js";
import { Alert } from "@mui/lab";
import { usePrompt } from "../Utilities/usePrompt.ts";
import { useProductsContext } from "../Contexts/productsContext";
import { useDistributorsContext } from "../Contexts/distributorsContext.jsx";
import { useSKUFulfillmentTypesContext } from "../Contexts/skuFulfillmentTypesContext";
import { PausedIndicator } from "./PausedIndicator";
import { buildForecastExportColumns, buildForecastExportRow } from "../Utilities/forecastExportColumns";
import PropTypes from 'prop-types';

const WEEK_COUNT = 9;
const WEEKS_BEYOND_END_IN_MARKET_DATE = 4;
const INPUT_MULTIPLIER_DIFFERENCE_FACTOR = 10;
const enablePauseIndicator = process.env.REACT_APP_ENABLE_PAUSE_INDICATOR === 'true';

const enableHistoricalSales = process.env.REACT_APP_ENABLE_HISTORICAL_SALES === 'true';

const isForecastAmountInputValid = (adjustedAmount, originalAmount) => (
    adjustedAmount <= originalAmount * INPUT_MULTIPLIER_DIFFERENCE_FACTOR &&
    adjustedAmount >= originalAmount / INPUT_MULTIPLIER_DIFFERENCE_FACTOR
);

export const weekEndDateForIndex = (inMarketDate, index) =>
    dayjs(inMarketDate).add(index, "week").endOf("week");

// The grid should never start on a week that's already passed. The first column is whichever is
// later: the current week or the in-market date. The header still shows the real in-market date -
// only the columns shift. Mirrors gridAnchorDates in ForecastsRepository.GetSeasonalForecastItems.
export const gridAnchorDate = (inMarketDate) => {
    const currentWeekStart = dayjs().startOf("week");
    const trueDate = dayjs(inMarketDate);
    return trueDate.isAfter(currentWeekStart) ? trueDate : currentWeekStart;
};

// How many weekly columns to show: from the anchor week through the end-in-market date plus
// WEEKS_BEYOND_END_IN_MARKET_DATE extra weeks, counting both ends. If the anchor is already past
// the real end date but still within those extra weeks, this just counts the weeks left. Falls
// back to WEEK_COUNT when there is no end-in-market date. Mirrors
// ForecastsRepository.SeasonalWeekCount so the columns line up with the data the backend sends.
export const seasonalWeekCount = (inMarketDate, endInMarketDate) => {
    if (!inMarketDate || !endInMarketDate) return WEEK_COUNT;
    const firstWeekEnd = dayjs(inMarketDate).endOf("week");
    const extendedLastWeekEnd = dayjs(endInMarketDate).endOf("week").add(WEEKS_BEYOND_END_IN_MARKET_DATE, "week");
    const weeks = extendedLastWeekEnd.diff(firstWeekEnd, "week") + 1;
    return weeks >= 1 ? weeks : WEEK_COUNT;
};

const matchForecast = (row, weekEnd) =>
    row.forecastAmounts?.find(fa => dayjs(fa.weekEndDate).endOf("week").isSame(weekEnd, "day"));

export const buildInMarketDateGroups = (forecastReviewItems) => {
    const map = new Map();
    forecastReviewItems.forEach(item => {
        const inMarketKey = item.inMarketDate
            ? dayjs(item.inMarketDate).startOf("day").format("YYYY-MM-DD")
            : "no-date";
        const endKey = item.endInMarketDate
            ? dayjs(item.endInMarketDate).startOf("day").format("YYYY-MM-DD")
            : "no-end";
        const key = `${inMarketKey}|${endKey}`;
        if (!map.has(key)) {
            map.set(key, { key, inMarketDate: item.inMarketDate, endInMarketDate: item.endInMarketDate, items: [] });
        }
        map.get(key).items.push(item);
    });

    return Array.from(map.values()).sort((a, b) => {
        if (!a.inMarketDate) return 1;
        if (!b.inMarketDate) return -1;
        const byStart = dayjs(a.inMarketDate).valueOf() - dayjs(b.inMarketDate).valueOf();
        if (byStart !== 0) return byStart;
        // Same in-market date: order by end-in-market date (groups without one sort last).
        if (!a.endInMarketDate) return 1;
        if (!b.endInMarketDate) return -1;
        return dayjs(a.endInMarketDate).valueOf() - dayjs(b.endInMarketDate).valueOf();
    });
};

// Historical/"Prior Year" rows are injected further down inside InMarketDateGroupGrid
// (rowsWithHistorical/expandedRows) and never flow back into forecastReviewItems, so building
// export sheets from these groups naturally excludes them.
export const buildSeasonalForecastExportSheets = (forecastReviewItems, skuFulfillmentTypes) => {
    const fulfillmentMethodById = new Map();
    (skuFulfillmentTypes ?? []).forEach(t => fulfillmentMethodById.set(t.id, t.description));

    return buildInMarketDateGroups(forecastReviewItems).map(group => {
        const anchorDate = gridAnchorDate(group.inMarketDate);
        const weekCount = seasonalWeekCount(anchorDate, group.endInMarketDate);
        const weekEndDates = Array.from({ length: weekCount }, (_, i) => weekEndDateForIndex(anchorDate, i));
        const startLabel = group.inMarketDate ? dayjs(group.inMarketDate).format("MM-DD-YYYY") : "No Date";
        const endLabel = group.endInMarketDate ? ` to ${dayjs(group.endInMarketDate).format("MM-DD-YYYY")}` : "";

        return {
            name: `${startLabel}${endLabel}`,
            columns: buildForecastExportColumns(weekEndDates, { includeEndInMarketDate: true }),
            rows: group.items.map(item => buildForecastExportRow(item, weekEndDates, fulfillmentMethodById, { includeEndInMarketDate: true })),
        };
    });
};

// Finds the prior-year number for one week column. The backend already moved each prior-year amount
// onto the current week it lines up with, and stores that date in `month`, so we match on it directly.
const matchDepletion = (row, weekEnd) =>
    row.depletions?.find(depletion => dayjs(depletion.month).endOf("week").isSame(weekEnd, "day"));

const InMarketDateGroupGrid = ({ inMarketDate, endInMarketDate, items, isReadOnly, loading, onEdited }) => {
    const apiRef = useGridApiRef();
    const { skuFulfillmentTypes } = useSKUFulfillmentTypesContext();

    const anchorDate = useMemo(() => gridAnchorDate(inMarketDate), [inMarketDate]);

    // True when the first column is the current week instead of the real in-market date.
    const firstColumnIsCurrentWeek = useMemo(
        () => anchorDate.isSame(dayjs().startOf("week"), "day"),
        [anchorDate]
    );

    const weekCount = useMemo(
        () => seasonalWeekCount(anchorDate, endInMarketDate),
        [anchorDate, endInMarketDate]
    );

    const weekEndDates = useMemo(() => (
        Array.from({ length: weekCount }, (_, i) => weekEndDateForIndex(anchorDate, i))
    ), [anchorDate, weekCount]);

    // Prior-year numbers we already downloaded, so we never ask for the same row twice.
    // Shape: { [rowId]: { loading, error, depletions } }
    const [historicalCache, setHistoricalCache] = useState({});
    // Ids of the rows whose ⓘ button is currently switched on.
    const [expandedRows, setExpandedRows] = useState([]);

    const historicalWeekStart = weekEndDates[0]?.format("YYYY-MM-DD");
    const historicalWeekEnd = weekEndDates[weekEndDates.length - 1]?.format("YYYY-MM-DD");

    useEffect(() => {
        if (!enableHistoricalSales) return;
        const controllers = [];
        expandedRows.forEach((rowId) => {
            if (historicalCache[rowId]) return;
            const row = items.find((r) => r.id === rowId);
            if (!row?.product?.sku) return;

            const controller = new AbortController();
            controllers.push(controller);
            setHistoricalCache((prev) => ({ ...prev, [rowId]: { loading: true } }));

            fetch(
                `/forecastreview/historicaldepletion?distributorId=${row.distributorId}` +
                `&sku=${encodeURIComponent(row.product.sku)}` +
                `&weekStart=${historicalWeekStart}&weekEnd=${historicalWeekEnd}`,
                { signal: controller.signal }
            )
                .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
                .then((data) =>
                    setHistoricalCache((prev) => ({
                        ...prev,
                        [rowId]: { loading: false, depletions: data.depletions ?? [] },
                    }))
                )
                .catch((err) => {
                    if (err?.name === "AbortError") return;
                    setHistoricalCache((prev) => ({
                        ...prev,
                        [rowId]: { loading: false, error: true, depletions: [] },
                    }));
                });
        });
        return () => controllers.forEach((c) => c.abort());
    }, [expandedRows]);

    // Builds the list of rows the grid shows: every product row, plus an extra read-only "Prior Year"
    // row right under the ones the user switched on. Its numbers appear in the F column, because this
    // grid has no separate depletions column. It needs no week labels of its own - the week headers at
    // the top already apply to it.
    // Important: the extra row is added here only, never saved back into the context. The save code
    // matches edited items to original items by position in the array, so an extra row would break it.
    const rowsWithHistorical = useMemo(() => {
        if (!enableHistoricalSales) return items;
        const out = [];
        for (const row of items) {
            out.push(row);
            if (!expandedRows.includes(row.id) || !row.product?.sku) continue;
            out.push({
                id: `hist-${row.id}`,
                isHistoricalRow: true,
                product: {
                    name: "Prior Year (Actual Depletions)",
                    sku: null,
                },
                distributorId: row.distributorId,
                fulfillmentMethodId: row.fulfillmentMethodId,
                forecastAmounts: [],
                depletions: historicalCache[row.id]?.depletions ?? [],
            });
        }
        return out;
    }, [items, expandedRows, historicalCache]);

    const fulfillmentMethodById = useMemo(() => {
        const map = new Map();
        (skuFulfillmentTypes ?? []).forEach(t => map.set(t.id, t.description));
        return map;
    }, [skuFulfillmentTypes]);

    const handleCellKeyDown = (params, event) => {
        if (event.key === "Tab") {
            event.preventDefault();
            event.defaultMuiPrevented = true;

            // Injected "Prior Year" rows are read-only, so Tab must step over them.
            const allRowIds = gridSortedRowIdsSelector(apiRef)
                .filter((id) => !apiRef.current.getRow(id)?.isHistoricalRow);
            const currentRowIndex = allRowIds.indexOf(params.id);
            const isShift = event.shiftKey;

            const editableColumns = apiRef.current
                .getAllColumns()
                .filter((col) => col.editable)
                .map((col) => col.field);

            if (editableColumns.length === 0) return;

            const currentColIndex = editableColumns.indexOf(params.field);
            let nextRowIndex = currentRowIndex;
            let nextColIndex = isShift ? currentColIndex - 1 : currentColIndex + 1;

            if (nextColIndex >= editableColumns.length) {
                nextColIndex = 0;
                nextRowIndex = currentRowIndex + 1;
            } else if (nextColIndex < 0) {
                nextColIndex = editableColumns.length - 1;
                nextRowIndex = currentRowIndex - 1;
            }

            if (nextRowIndex >= 0 && nextRowIndex < allRowIds.length) {
                const nextRowId = allRowIds[nextRowIndex];
                const nextField = editableColumns[nextColIndex];
                apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
                setTimeout(() => {
                    apiRef.current.scrollToIndexes({
                        rowIndex: nextRowIndex,
                        colIndex: apiRef.current.getAllColumns().findIndex((col) => col.field === nextField),
                    });
                    apiRef.current.setCellFocus(nextRowId, nextField);
                    apiRef.current.startCellEditMode({ id: nextRowId, field: nextField });
                }, 50);
            }
        }
    };

    const columnDefinitions = useMemo(() => {
        const baseColumns = [
        ...(enablePauseIndicator ? [{
            field: "paused",
            headerName: "",
            headerAlign: "center",
            align: "center",
            width: 36,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (params.row.paused ? <PausedIndicator size={20} /> : null),
        }] : []),
        {
            field: "product.name",
            headerName: "Product",
            headerAlign: "center",
            width: 400,
            valueGetter: (params) => params.row.product?.name,
        },
        ...(enableHistoricalSales ? [{
            field: "last4weeks",
            headerName: "",
            width: 36,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                if (!params.row.product?.sku) return null;
                const isExpanded = expandedRows.includes(params.id);
                return (
                    <Tooltip title="Historical Sales" arrow>
                        <IconButton
                            size="small"
                            sx={{ color: isExpanded ? "primary.dark" : "primary.main", p: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpandedRows(prev =>
                                    prev.includes(params.id)
                                        ? prev.filter(id => id !== params.id)
                                        : [...prev, params.id]
                                );
                            }}
                        >
                            <InfoIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                    </Tooltip>
                );
            },
        }] : []),
        {
            field: "distributorSapId",
            headerName: "Distributor",
            headerAlign: "center",
            width: 90,
            valueGetter: (params) => params.row.distributorSapId,
        }, {
            field: "product.sku",
            headerName: "BBC",
            headerAlign: "center",
            width: 80,
            valueGetter: (params) => params.row.product?.sku,
        }, {
            field: "customerPartNumber",
            headerName: "Customer",
            headerAlign: "center",
            width: 90,
            valueGetter: (params) => params.row.customerPartNumber,
        }, {
            field: "fulfillmentMethod",
            headerName: "Fulfillment Method",
            headerAlign: "center",
            width: 130,
            valueGetter: (params) => fulfillmentMethodById.get(params.row.fulfillmentMethodId) ?? "",
        }];

        for (let i = 0; i < weekEndDates.length; i++) {
            const weekEnd = weekEndDates[i];
            const weekLabel = weekEnd.format("MM/DD/YY");

            baseColumns.push({
                field: `forecastAmount[${i}].originalAmount`,
                headerName: "F",
                width: 60,
                type: "number",
                headerAlign: "center",
                align: "right",
                valueGetter: (params) => {
                    // The "Prior Year" row carries prior-year actual depletions, not a forecast. The
                    // seasonal grid has no D column, so they are shown here under F.
                    if (params.row?.isHistoricalRow) {
                        const depletion = matchDepletion(params.row, weekEnd);
                        return isNaN(depletion?.amount) ? "" : Math.round(depletion?.amount);
                    }

                    const found = matchForecast(params.row, weekEnd);
                    if (!found || found.originalAmount == null || isNaN(found.originalAmount)) return "";
                    return Math.round(found.originalAmount);
                },
            });
            baseColumns.push({
                field: `forecastAmount[${i}].adjustedAmount`,
                headerName: "A",
                width: 75,
                editable: !isReadOnly && !(i === 0 && firstColumnIsCurrentWeek),
                type: "number",
                headerAlign: "center",
                renderCell: (params) => (
                    <Tooltip title={weekLabel} placement="top" arrow>
                        <span style={{ width: '100%', textAlign: 'right' }}>
                            {params.formattedValue}
                        </span>
                    </Tooltip>
                ),
                valueGetter: (params) => {
                    const found = matchForecast(params.row, weekEnd);
                    if (!found || found.forecastAdjustmentId === 0 || found.adjustedAmount == null || isNaN(found.adjustedAmount)) return "";
                    return Math.round(found.adjustedAmount);
                },
                valueSetter: (params) => {
                    if (params.row?.isHistoricalRow) {
                        return params.row;
                    }

                    const found = matchForecast(params.row, weekEnd);
                    const parsed = parseInt(params.value);
                    const valid = isInt(parsed) && parsed >= 0;

                    if (valid) {
                        if (found) {
                            found.adjustedAmount = parsed;
                        } else {
                            params.row.forecastAmounts.push({
                                weekEndDate: weekEnd.toDate(),
                                weekId: weekEnd.valueOf(),
                                adjustedAmount: parsed,
                                originalAmount: null,
                                forecastAdjustmentId: null,
                            });
                        }
                    } else if (found) {
                        found.adjustedAmount = null;
                    } else {
                        params.row.forecastAmounts.push({
                            weekEndDate: weekEnd.toDate(),
                            weekId: weekEnd.valueOf(),
                            adjustedAmount: null,
                            originalAmount: null,
                            forecastAdjustmentId: null,
                        });
                    }
                    return params.row;
                },
            });
        }

        return baseColumns;
    }, [weekEndDates, isReadOnly, firstColumnIsCurrentWeek, fulfillmentMethodById, expandedRows]);

    const columnGroupingModel = useMemo(() => {
        const forecastsGroup = {
            groupId: "forecasts",
            description: "Forecast",
            headerAlign: "left",
            renderHeaderGroup: () => (
                <div className="MuiDataGrid-columnHeaderTitle css-t89xny-MuiDataGrid-columnHeaderTitle">
                    <Stack direction="horizontal" alignItems="center">
                        <Typography variant="h5">
                            <strong>(F) Depletions Forecast &amp; (A) Customer Adjustments (Units)</strong>
                        </Typography>
                    </Stack>
                </div>
            ),
            children: [],
        };

        for (let i = 0; i < weekEndDates.length; i++) {
            const label = weekEndDates[i].format("MM/DD/YY");
            forecastsGroup.children.push({
                groupId: `Week ${i + 1}`,
                headerAlign: "center",
                renderHeaderGroup: () => (
                    <span>{label}</span>
                ),
                children: [
                    { field: `forecastAmount[${i}].originalAmount` },
                    { field: `forecastAmount[${i}].adjustedAmount` },
                ],
            });
        }

        return [{
            groupId: "SKU",
            description: "SKU",
            headerAlign: "center",
            children: [{ field: "product.sku" }, { field: "customerPartNumber" }],
        }, forecastsGroup];
    }, [weekEndDates]);

    return (
        <Accordion defaultExpanded disableGutters sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#f5f5f5" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {"In-Market Date: "}
                    <Box component="span" sx={{ fontWeight: "normal", color: "success.main" }}>
                        {inMarketDate ? dayjs(inMarketDate).format("MM/DD/YYYY") : "-"}
                    </Box>
                    {"  End In-Market Date: "}
                    <Box component="span" sx={{ fontWeight: "normal", color: "error.main" }}>
                        {endInMarketDate ? dayjs(endInMarketDate).endOf("week").format("MM/DD/YYYY") : "-"}
                    </Box>
                    {` (${items.length} ${items.length === 1 ? "item" : "items"})`}

                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                <DataGridPremium
                autoHeight
                hideFooter
                rows={rowsWithHistorical}
                columns={columnDefinitions}
                apiRef={apiRef}
                columnGroupingModel={columnGroupingModel}
                loading={loading}
                disableColumnMenu
                rowHeight={30}
                columnHeaderHeight={30}
                experimentalFeatures={{ columnGrouping: true, newEditingApi: true }}
                isCellEditable={(params) => {
                    if (params.row?.isHistoricalRow) return false;
                    if (isReadOnly) return false;
                    const match = params.field.match(/\[(\d+)\]/);
                    if (!match) return false;
                    const idx = parseInt(match[1], 10);
                    if (idx === 0 && firstColumnIsCurrentWeek) return false;
                    const found = matchForecast(params.row, weekEndDates[idx]);
                    return found && found.originalAmount != null && !isNaN(parseInt(found.originalAmount));
                }}
                onCellEditStart={(_params, event) => {
                    if (event.type != "dblclick") {
                        event.preventDefault();
                        event.defaultMuiPrevented = true;
                    }
                }}
                onCellKeyDown={handleCellKeyDown}
                onCellEditStop={(params) => {
                    setTimeout(() => {
                        const match = params.field.match(/\[(\d+)\]/);
                        if (!match) return;
                        const idx = parseInt(match[1], 10);
                        const found = matchForecast(params.row, weekEndDates[idx]);
                        if (found && found.originalAmount != null && found.adjustedAmount != null &&
                            !isForecastAmountInputValid(found.adjustedAmount, found.originalAmount)) {
                            alert("The adjustment made is +/- 10x than the current forecast.  Please verify this adjustment is correct.");
                        }
                    }, 150);
                    onEdited();
                }}
                slotProps={{
                    cell: {
                        onFocus: event => {
                            if (event.target?.tagName.toLowerCase() === "input") {
                                event.target?.select();
                            }
                        },
                    },
                }}
                getRowClassName={(params) => (params.row?.isHistoricalRow ? "historicalRow" : "")}
                getCellClassName={(params) => {
                    if (params.row?.isHistoricalRow) return "locked";

                    if (params.field === "forecastAmount[0].adjustedAmount" && firstColumnIsCurrentWeek) return "locked";

                    return params.field.includes("adjusted")
                        ? (isInt(params.value) || params.value === " ") ? "adjustable" : "adjusted"
                        : " ";
                }}
                />
            </AccordionDetails>
        </Accordion>
    );
};

InMarketDateGroupGrid.propTypes = {
    inMarketDate: PropTypes.any,
    endInMarketDate: PropTypes.any,
    items: PropTypes.array.isRequired,
    isReadOnly: PropTypes.bool,
    loading: PropTypes.bool,
    onEdited: PropTypes.func.isRequired,
};

export const SeasonalForecastGrid = () => {
    const {
        forecastReviewItems,
        SaveForecastAdjustmentChanges,
        isReadOnly,
        error,
    } = useSeasonalForecastContext();
    const { setSelectedProducts } = useProductsContext();
    const { selectedDistributors } = useDistributorsContext();
    const [edited, setEdited] = useState(false);
    const [sending, setSending] = useState(false);

    usePrompt("You have unsaved changes. Are you sure you want to leave?", edited);

    const groups = useMemo(() => buildInMarketDateGroups(forecastReviewItems), [forecastReviewItems]);

    return (<div>
        {isReadOnly ? (
            <Stack direction="row" alignItems="center" justifyContent="center">
                <Alert variant="filled" severity="info" sx={{ alignItems: 'center' }}>
                    {selectedDistributors.length > 1
                        ? <Typography variant="h6">One of the selected distributors uses an automated system for forecast adjustment. This view is read only.</Typography>
                        : <Typography variant="h6">The selected distributor uses an automated system for forecast adjustment. This view is read only.</Typography>}
                </Alert>
            </Stack>
        ) : null}

        {error ? (
            <Alert variant="filled" severity="error" action={
                <Button color="inherit" size="small" onClick={() => window.location.reload()}>Refresh Page</Button>
            }>
                Updates have failed! Refresh the page before trying again.
            </Alert>
        ) : (<Box sx={{
            height: "calc(100vh - 280px)", width: "100%", padding: 2,
            display: "flex", flexDirection: "column",
            "& .adjustable": { backgroundColor: "#d2f7d4", color: "#000000" },
            "& .adjusted": { backgroundColor: "#c1f7ec", color: "#000000" },
            "& .banded": { backgroundColor: "#a9dbfc", color: "#000000" },
            "& .locked": { backgroundColor: "#EBEBE4", color: "#000000" },
            "& .historicalRow": { fontWeight: "bold", fontStyle: "italic" },
        }}>
            <Alert style={{ marginBottom: 8 }} severity="warning">
                Note that the forecasted quantity displayed is representative of the forecast for both direct and any partnered indirect wholesalers. Please ensure any forecast adjustments also account for indirect wholesalers.
            </Alert>

            <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
                {groups.map(group => (
                    <InMarketDateGroupGrid
                        key={group.key}
                        inMarketDate={group.inMarketDate}
                        endInMarketDate={group.endInMarketDate}
                        items={group.items}
                        isReadOnly={isReadOnly}
                        loading={sending}
                        onEdited={() => setEdited(true)}
                    />
                ))}
            </Box>

            <Box sx={{
                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                backgroundColor: "background.paper",
                pt: 2, mt: 1,
            }}>
                <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={2}>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            setSending(true);
                            await SaveForecastAdjustmentChanges();
                            setSending(false);
                            setEdited(false);
                            setSelectedProducts([]);
                        }}
                        disabled={!edited}
                    >
                        Submit
                    </Button>
                    <Typography variant="body">
                        I acknowledge and agree that BBC will generate shipments based upon reported wholesaler inventory and forecasted depletions, subject to change pending transportation and product availability.
                    </Typography>
                    {sending && <LinearProgress size={24} />}
                </Stack>
            </Box>
        </Box>)}
    </div>);
};

SeasonalForecastGrid.propTypes = {};
